import React from 'react';

/**
 * CaseStudiesPage component for the Agent Nexus
 * 
 * This page showcases success stories and case studies from organizations
 * that have implemented the Agent Nexus platform, including:
 * - Featured case studies with detailed metrics
 * - Industry-specific success stories
 * - Testimonials from clients
 * 
 * The design follows the Agent Nexus branding guidelines with consistent
 * use of colors, typography, and component styling.
 */
const CaseStudiesPage = () => {
  // Case studies data
  const caseStudies = [
    {
      id: 'acme-corporation',
      title: 'How Acme Corporation Automated 75% of Their Workflow',
      company: 'Acme Corporation',
      industry: 'Manufacturing',
      logo: '/assets/images/case-studies/acme-logo.png',
      featured: true,
      metrics: [
        { label: 'Productivity Increase', value: '45%' },
        { label: 'Cost Reduction', value: '32%' },
        { label: 'ROI', value: '287%' }
      ],
      excerpt: 'Acme Corporation implemented Agent Nexus to streamline their manufacturing processes, resulting in significant productivity gains and cost savings.',
      testimonial: {
        quote: 'Agent Nexus has transformed how we operate. The automation capabilities have freed up our team to focus on innovation rather than repetitive tasks.',
        author: 'Jane Smith',
        title: 'CTO, Acme Corporation'
      }
    },
    {
      id: 'global-financial',
      title: 'Global Financial Services Firm Enhances Customer Experience with AI',
      company: 'Global Financial Services',
      industry: 'Finance',
      logo: '/assets/images/case-studies/global-financial-logo.png',
      featured: true,
      metrics: [
        { label: 'Customer Satisfaction', value: '+28%' },
        { label: 'Response Time', value: '-65%' },
        { label: 'Operational Costs', value: '-22%' }
      ],
      excerpt: 'A leading financial services firm leveraged Agent Nexus to automate customer service processes and enhance the overall customer experience.',
      testimonial: {
        quote: 'The implementation of Agent Nexus has allowed us to provide faster, more accurate service to our customers while significantly reducing our operational costs.',
        author: 'Michael Johnson',
        title: 'Head of Operations, Global Financial Services'
      }
    },
    {
      id: 'tech-innovations',
      title: 'Tech Innovations Streamlines Development Pipeline',
      company: 'Tech Innovations',
      industry: 'Technology',
      logo: '/assets/images/case-studies/tech-innovations-logo.png',
      featured: false,
      metrics: [
        { label: 'Development Time', value: '-40%' },
        { label: 'Code Quality', value: '+35%' },
        { label: 'Release Frequency', value: '+150%' }
      ],
      excerpt: 'Tech Innovations implemented Agent Nexus to automate their development and testing processes, resulting in faster releases and higher quality code.',
      testimonial: {
        quote: 'Agent Nexus has revolutionized our development process. We\'re now able to ship features faster with fewer bugs, which has been a game-changer for our business.',
        author: 'Sarah Chen',
        title: 'VP of Engineering, Tech Innovations'
      }
    },
    {
      id: 'retail-giant',
      title: 'Retail Giant Optimizes Supply Chain with Agent Nexus',
      company: 'Retail Giant',
      industry: 'Retail',
      logo: '/assets/images/case-studies/retail-giant-logo.png',
      featured: false,
      metrics: [
        { label: 'Inventory Accuracy', value: '+42%' },
        { label: 'Fulfillment Speed', value: '+58%' },
        { label: 'Supply Chain Costs', value: '-25%' }
      ],
      excerpt: 'A major retail company used Agent Nexus to optimize their supply chain operations, improving inventory management and reducing costs.',
      testimonial: {
        quote: 'The visibility and automation that Agent Nexus provides has transformed our supply chain operations. We\'ve seen dramatic improvements in efficiency and accuracy.',
        author: 'Robert Williams',
        title: 'Supply Chain Director, Retail Giant'
      }
    },
    {
      id: 'edu-systems',
      title: 'Educational Systems Enhances Student Experience',
      company: 'Educational Systems',
      industry: 'Education',
      logo: '/assets/images/case-studies/edu-systems-logo.png',
      featured: false,
      metrics: [
        { label: 'Administrative Time', value: '-60%' },
        { label: 'Student Satisfaction', value: '+38%' },
        { label: 'Resource Utilization', value: '+45%' }
      ],
      excerpt: 'Educational Systems implemented Agent Nexus to streamline administrative processes and improve the student experience across their institutions.',
      testimonial: {
        quote: 'Agent Nexus has allowed our staff to focus more on students and less on paperwork. The automation capabilities have transformed how we operate.',
        author: 'Dr. Emily Rodriguez',
        title: 'Chief Innovation Officer, Educational Systems'
      }
    },
    {
      id: 'healthcare-provider',
      title: 'Healthcare Provider Improves Patient Care with Automation',
      company: 'Healthcare Provider',
      industry: 'Healthcare',
      logo: '/assets/images/case-studies/healthcare-provider-logo.png',
      featured: false,
      metrics: [
        { label: 'Administrative Burden', value: '-55%' },
        { label: 'Patient Satisfaction', value: '+40%' },
        { label: 'Care Coordination', value: '+62%' }
      ],
      excerpt: 'A leading healthcare provider implemented Agent Nexus to automate administrative tasks and improve care coordination across their network.',
      testimonial: {
        quote: 'By automating routine tasks with Agent Nexus, our clinical staff can spend more time with patients. The impact on both staff satisfaction and patient care has been remarkable.',
        author: 'Dr. James Wilson',
        title: 'Chief Medical Officer, Healthcare Provider'
      }
    }
  ];

  // Function to handle image loading errors
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = 'https://via.placeholder.com/150x50?text=Logo';
  };

  return (
    <div className="case-studies-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Success Stories
          </h1>
          <p className="text-xl mb-8 text-primary-100 max-w-3xl">
            Discover how organizations across industries are using Agent Nexus to transform their operations, improve efficiency, and drive innovation.
          </p>
        </div>
      </section>

      {/* Featured Case Studies Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Featured Case Studies
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {caseStudies.filter(study => study.featured).map((study, index) => (
              <div 
                key={index} 
                className="case-study-card bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-4">
                      <img 
                        src={study.logo} 
                        alt={`${study.company} logo`} 
                        className="max-w-full max-h-full p-2"
                        onError={handleImageError}
                      />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400 block">
                        {study.industry}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {study.company}
                      </h3>
                    </div>
                  </div>
                  
                  <h4 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    {study.title}
                  </h4>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {study.excerpt}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {study.metrics.map((metric, i) => (
                      <div key={i} className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span className="block text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {metric.value}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {metric.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <blockquote className="italic text-gray-600 dark:text-gray-300 border-l-4 border-primary-500 pl-4 py-2 mb-4">
                    "{study.testimonial.quote}"
                    <footer className="mt-2 text-sm">
                      <strong>{study.testimonial.author}</strong>, {study.testimonial.title}
                    </footer>
                  </blockquote>
                  
                  <a 
                    href={`/case-studies/${study.id}`} 
                    className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 inline-flex items-center"
                  >
                    Read Full Case Study
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Case Studies Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            More Success Stories
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.filter(study => !study.featured).map((study, index) => (
              <div 
                key={index} 
                className="case-study-card bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                      <img 
                        src={study.logo} 
                        alt={`${study.company} logo`} 
                        className="max-w-full max-h-full p-2"
                        onError={handleImageError}
                      />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-primary-600 dark:text-primary-400 block">
                        {study.industry}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {study.company}
                      </h3>
                    </div>
                  </div>
                  
                  <h4 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                    {study.title}
                  </h4>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                    {study.excerpt}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {study.metrics.map((metric, i) => (
                      <div key={i} className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span className="block text-lg font-bold text-primary-600 dark:text-primary-400">
                          {metric.value}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {metric.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <a 
                    href={`/case-studies/${study.id}`} 
                    className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 inline-flex items-center text-sm"
                  >
                    Read Full Case Study
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-900 dark:text-white">
            Solutions by Industry
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Explore how Agent Nexus is helping organizations across different industries solve their unique challenges.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Manufacturing', 'Finance', 'Technology', 'Retail', 'Education', 'Healthcare'].map((industry, index) => (
              <a 
                key={index} 
                href={`/solutions/${industry.toLowerCase()}`} 
                className="industry-card bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center hover:bg-primary-50 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
              >
                <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                  {industry}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  View solutions
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
            Ready to become our next success story?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Join thousands of organizations already using Agent Nexus to transform their operations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a href="/signup" className="btn-primary px-8 py-3 text-lg">
              Start Free Trial
            </a>
            <a href="/contact" className="btn-outline px-8 py-3 text-lg">
              Request Demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage; 